import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslations } from 'next-intl';

import {
  Button,
  DropdownContent,
  DropdownItem,
  DropdownMenu,
  DropdownPortal,
  DropdownTrigger,
  FormControl,
  TextField,
} from '@zealy/design-system';
import { CrossLine, PlusLine } from '@zealy/icons';

const access = (path: string, object: any): string | undefined => {
  return path.split('.').reduce((o, i) => o?.[i], object);
};

type MetricType = 'minLikes' | 'minImpressions' | 'minReplies' | 'minRetweets';

const METRICS: { key: MetricType; label: string }[] = [
  { key: 'minLikes', label: 'Minimum Likes' },
  { key: 'minImpressions', label: 'Minimum Impressions' },
  { key: 'minReplies', label: 'Minimum Replies' },
  { key: 'minRetweets', label: 'Minimum Retweets' },
];

interface ActiveMetric {
  key: MetricType;
  value: number;
}

export const EngagementMetricsField = ({
  name,
  namespace,
  optional,
}: {
  name: string;
  namespace: string;
  optional?: boolean;
}) => {
  const t = useTranslations('quest.type');
  const {
    register,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext();

  const currentValues = watch(name) || {};

  // Track which metrics are currently added (have a non-undefined value)
  const [activeMetrics, setActiveMetrics] = useState<ActiveMetric[]>(() => {
    return METRICS.map(m => ({ key: m.key, value: currentValues[m.key] || 0 })).filter(
      m => currentValues[m.key] !== undefined,
    );
  });

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const label = t(`${namespace}.label`);

  const availableMetrics = METRICS.filter(m => !activeMetrics.find(am => am.key === m.key));

  const addMetric = (metricKey: MetricType) => {
    const newMetric = { key: metricKey, value: 0 };
    setActiveMetrics([...activeMetrics, newMetric]);
    setValue(`${name}.${metricKey}`, 0, { shouldDirty: true, shouldValidate: true });
    console.log('[EngagementMetrics] Added metric:', metricKey, 'to path:', `${name}.${metricKey}`);
    setDropdownOpen(false);
  };

  const removeMetric = (metricKey: MetricType) => {
    setActiveMetrics(activeMetrics.filter(m => m.key !== metricKey));
    setValue(`${name}.${metricKey}`, undefined, { shouldDirty: true, shouldValidate: true });
    console.log(
      '[EngagementMetrics] Removed metric:',
      metricKey,
      'from path:',
      `${name}.${metricKey}`,
    );
  };

  const metricCount = activeMetrics.length;

  return (
    <>
      <FormControl
        name={name}
        label={
          <div className="flex items-center gap-200">
            <span>{label}</span>
            {metricCount > 0 && (
              <span className="flex items-center justify-center w-[24px] h-[24px] rounded-full bg-component-tertiary text-primary body-component-sm">
                {metricCount}
              </span>
            )}
          </div>
        }
        optional={optional}
      >
        <div className="flex flex-col gap-200">
          {/* Existing Metrics - Regular Form Fields */}
          {activeMetrics.map(metric => {
            const error = access(`${name}.${metric.key}.message`, errors);
            return (
              <div key={metric.key} className="group flex gap-200 items-start">
                <div className="flex-1">
                  <TextField
                    type="number"
                    label={
                      <span className="text-secondary">
                        {t(`${namespace}.fields.${metric.key}.label`)}
                      </span>
                    }
                    placeholder="0"
                    isInvalid={!!error}
                    hint={error}
                    {...register(`${name}.${metric.key}`, { valueAsNumber: true })}
                  />
                </div>
                <Button
                  type="button"
                  onClick={() => removeMetric(metric.key)}
                  onlyIcon
                  leftIcon={<CrossLine size={16} />}
                  variant="ghost"
                  size="sm"
                  className="mt-[24px] opacity-0 group-hover:opacity-100 text-tertiary hover:text-primary"
                  aria-label="Delete"
                />
              </div>
            );
          })}

          {/* Add Metric Button with Dropdown */}
        </div>
      </FormControl>
      {availableMetrics.length > 0 && (
        <div className="border-2 border-dashed border-separators rounded-lg overflow-hidden -mt-200">
          <DropdownMenu
            name="add-engagement-metric"
            open={dropdownOpen}
            onOpenChange={setDropdownOpen}
          >
            <DropdownTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                leftIcon={<PlusLine />}
                className="w-full py-200 justify-center text-secondary hover:text-primary rounded-none"
              >
                Add Metric
              </Button>
            </DropdownTrigger>
            <DropdownPortal>
              <DropdownContent
                style={{ width: 'var(--radix-dropdown-menu-trigger-width)' }}
                className="min-w-0"
              >
                {availableMetrics.map(metric => (
                  <DropdownItem key={metric.key} onClick={() => addMetric(metric.key)}>
                    <span>{t(`${namespace}.fields.${metric.key}.label`)}</span>
                  </DropdownItem>
                ))}
              </DropdownContent>
            </DropdownPortal>
          </DropdownMenu>
        </div>
      )}
    </>
  );
};
